/**
 * Stop pinch-zoom in the native app, everywhere.
 *
 * WKWebView ignores `user-scalable=no` in most cases, and a per-element
 * `touch-action` only covers the subtree it is on, so views that scroll in
 * their own container, portal to <body>, or set `touch-action: manipulation`
 * could still be pinched into a zoomed, broken-looking page. Rather than
 * chase each surface, lock it at the document level:
 *
 *  1. Viewport meta: `maximum-scale=1, user-scalable=no` (honored by Android's
 *     WebView and some iOS versions).
 *  2. WebKit `gesture*` events (iOS pinch) are cancelled.
 *  3. CSS `touch-action: pan-x pan-y` on the document (main.css) leaves no
 *     pinch to begin with.
 *
 * There is deliberately no document `touchmove` listener: a non-passive one
 * makes every scroll on every scroller wait for the main thread before it
 * can start (visible as stutter while a reply streams), and the three layers
 * above already cover pinch.
 *
 * A surface that genuinely needs pinch (a future zoomable image viewer, say)
 * can opt out with `data-allow-pinch` on itself or an ancestor.
 */
import { isNativePlatform } from './platform'

let installed = false

function allowsPinch(target: EventTarget | null): boolean {
    return target instanceof Element && !!target.closest('[data-allow-pinch]')
}

export function installNativeZoomLock() {
    if (installed || typeof document === 'undefined' || !isNativePlatform()) return
    installed = true

    const meta = document.querySelector('meta[name="viewport"]')
    if (meta) {
        const content = meta.getAttribute('content') || ''
        if (!/user-scalable/.test(content)) {
            meta.setAttribute('content', `${content}, maximum-scale=1, user-scalable=no`)
        }
    }

    const block = (e: Event) => {
        if (allowsPinch(e.target)) return
        if (e.cancelable) e.preventDefault()
    }
    for (const type of ['gesturestart', 'gesturechange', 'gestureend']) {
        document.addEventListener(type, block, { passive: false, capture: true })
    }
}
